import { useContext, useEffect, useState } from 'react';
import { Progress } from '@backstage/core-components';
import { gql, useQuery } from '@apollo/client';
import { UserContext } from '../../contexts/UserContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DevHubAlert, DevHubDynamicIcon } from '@devxdiscover/devhub-ui';

/**
 * Query for the DevHub monitors.
 */
export const GetDevHubMonitorsQuery = gql`
  query MonitorsQuery($filter: DevHubMonitorFilter) {
    devhub {
      monitors(filter: $filter) {
        checkName
        env
        state
        status
        numNotifications
        stateTransition
        lastNotification
        lastUpdated
        error
      }
    }
  }
`;

export type MonitorType = {
  checkName: string;
  env: string;
  state: string;
  status: string;
  numNotifications: number;
  stateTransition: string;
  lastNotification: string;
  lastUpdated: string;
  error: string;
};

export type DevHubMonitors = {
  devhub: {
    monitors: MonitorType[];
  };
};

/**
 * A component that displays the cloud accounts associated with an application.
 */
export const Monitors = () => {
  const { login } = useContext(UserContext);
  const [monitors, setMonitors] = useState<MonitorType[]>([]);

  const {
    loading: monitorsLoading,
    error: monitorsError,
    data: monitorData,
  } = useQuery<DevHubMonitors>(GetDevHubMonitorsQuery, {
    errorPolicy: 'all',
    variables: {
      login: login,
      filter: {
        environment: 'prod',
      },
    },
  });

  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  useEffect(() => {
    const accounts = monitorData?.devhub?.monitors || [];
    setMonitors(accounts);
  }, [monitorData]);

  if (monitorsLoading) {
    return <Progress />;
  } else if (monitorsError) {
    return <DevHubAlert severity="error">Error loading monitor</DevHubAlert>;
  }

  return (
    <DataTable
      value={monitors}
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 20]}
      style={{ fontSize: '16px', lineHeight: '1' }}
    >
      <Column field="checkName" header="Check" />
      <Column field="env" header="Environment" />
      <Column
        field="status"
        header="Status"
        body={(rowData: MonitorType) =>
          rowData?.status === 'ok' || rowData?.status === 'confirmation' ? (
            <DevHubDynamicIcon iconName="CheckCircle" color="success" />
          ) : (
            <DevHubDynamicIcon iconName="ReportProblem" color="error" />
          )
        }
      />

      <Column
        field="lastUpdated"
        header="Last Updated"
        body={(rowData: MonitorType) =>
          new Date(rowData.lastUpdated).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          })
        }
      />
      <Column
        field="stateTransition"
        header="State Transition"
        body={(rowData: MonitorType) =>
          new Date(rowData.stateTransition).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          })
        }
      />
      <Column field="numNotifications" header="#" style={{ width: '5%' }} />
      <Column
        field="lastNotification"
        header="Last Notification"
        body={(rowData: MonitorType) =>
          new Date(rowData.lastNotification).toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          })
        }
      />
    </DataTable>
  );
};
